/**
 * Pliable's own auth context.
 *
 * Replaces FronteggProvider for tenants using Pliable-managed auth (OIDC, etc.).
 * No Frontegg SDK loaded, no background refresh loops, no cookie-based session
 * management. Auth state comes from the JWT in sessionStorage.
 *
 * Provides the same hook shape as @frontegg/react so useAppAuth.ts can
 * consume either provider without conditional hooks.
 */
import { createContext, useContext, useMemo, useState, useCallback, type ReactNode } from 'react';
import AuthService, { type PlbAuthState as AuthServicePlbAuthState } from '../services/auth.service';

interface PliableUser {
  id: string;
  sub: string;
  name: string;
  email: string;
  tenantId: string;
  tenantIds: string[];
  roles: string[];
  permissions: string[];
  accessToken: string;
  profilePictureUrl: string;
  verified: boolean;
  mfaEnrolled: boolean;
  activatedForTenant: boolean;
  tenants: Array<{ tenantId: string; name: string }>;
  metadata: string;
}

interface PliableAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: PliableUser | undefined;
}

interface PliableAuthActions {
  logout: () => void;
  switchTenant: (params: { tenantId: string }) => void;
}

interface PliableTenantsState {
  tenants: Array<{ tenantId: string; name: string }>;
  loading: boolean;
}

const PliableAuthContext = createContext<{
  auth: PliableAuthState;
  actions: PliableAuthActions;
  tenantsState: PliableTenantsState;
  // Session setter — Embed.tsx calls this after /auth-exchange so
  // provider children (DashboardDetail, etc.) see the authenticated
  // state immediately on next render. Also mutates authService.*
  // so ApiService.getAccessToken() sees the new token.
  setEmbedSession: (token: string, authState: AuthServicePlbAuthState) => void;
} | null>(null);

function decodeJwt(token: string): any {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}

export function PliableAuthProvider({ children }: { children: ReactNode }) {
  const authService = AuthService.getInstance();

  // React-tracked mirror of authService.{handoffToken, plbAuthState}. We
  // initialize from authService so page-refresh recovery (embed-session
  // cookie + OIDC session cookie, both populated in the AuthService
  // constructor) shows up on first render. Embed.tsx updates us via
  // `setEmbedSession` so new auth state propagates without a full reload.
  const [token, setToken] = useState<string | null>(() => authService.handoffToken);
  const [authState, setAuthStateReact] = useState<AuthServicePlbAuthState | null>(
    () => authService.getAuthState(),
  );

  const setEmbedSession = useCallback(
    (t: string, s: AuthServicePlbAuthState) => {
      // Mirror to the singleton so ApiService.getAccessToken() and other
      // non-React consumers see the new token.
      authService.handoffToken = t;
      authService.setAuthState(s);
      setToken(t);
      setAuthStateReact(s);
      // Flip any `useAuthToken`-gated queries (e.g. useTenantConfig)
      // from disabled → enabled now that we have a token.
      authService.notifyAuthChanged();
    },
    [authService],
  );

  const auth = useMemo<PliableAuthState>(() => {
    if (!token || !authState) {
      return { isAuthenticated: false, isLoading: false, user: undefined };
    }

    // Check expiry
    const claims = decodeJwt(token);
    if (claims.exp && claims.exp * 1000 < Date.now()) {
      return { isAuthenticated: false, isLoading: false, user: undefined };
    }

    return {
      isAuthenticated: true,
      isLoading: false,
      user: {
        id: authState.sub,
        sub: authState.sub,
        name: authState.name,
        email: authState.email,
        tenantId: authState.tenant_id,
        tenantIds: [authState.tenant_id],
        roles: authState.roles || [],
        permissions: [],
        accessToken: authState.access_token,
        profilePictureUrl: authState.profile_img_url || '',
        verified: true,
        mfaEnrolled: false,
        activatedForTenant: true,
        tenants: authState.tenants || [],
        metadata: '',
      },
    };
  }, [token, authState]);

  const actions = useMemo<PliableAuthActions>(() => ({
    logout: () => {
      authService.handoffToken = null;
      authService.plbAuthState = null;
      if (authService.oidcLogoutUrl) {
        window.location.href = authService.oidcLogoutUrl;
      } else {
        window.location.replace('/login');
      }
    },
    switchTenant: () => {
      // Tenant switching not supported in OIDC mode — single tenant per domain
    },
  }), [authService]);

  const tenantsState = useMemo<PliableTenantsState>(() => ({
    tenants: authState?.tenants?.length
      ? authState.tenants
      : authState ? [{ tenantId: authState.tenant_id, name: authState.tenant_name || authState.tenant_id }] : [],
    loading: false,
  }), [authState]);

  // Memoize the context value so consumers don't re-render on every
  // provider render — important because context-consumer Sidebar et al.
  // pass callbacks into Radix, which throws "Maximum update depth"
  // when the callbacks' identities churn.
  const contextValue = useMemo(
    () => ({ auth, actions, tenantsState, setEmbedSession }),
    [auth, actions, tenantsState, setEmbedSession],
  );

  return (
    <PliableAuthContext.Provider value={contextValue}>
      {children}
    </PliableAuthContext.Provider>
  );
}

export function usePliableAuth(): PliableAuthState {
  const ctx = useContext(PliableAuthContext);
  if (!ctx) throw new Error('usePliableAuth must be used within PliableAuthProvider');
  return ctx.auth;
}

export function useSetEmbedSession() {
  const ctx = useContext(PliableAuthContext);
  if (!ctx) throw new Error('useSetEmbedSession must be used within PliableAuthProvider');
  return ctx.setEmbedSession;
}

export function usePliableAuthActions(): PliableAuthActions {
  const ctx = useContext(PliableAuthContext);
  if (!ctx) throw new Error('usePliableAuthActions must be used within PliableAuthProvider');
  return ctx.actions;
}

export function usePliableTenantsState(): PliableTenantsState {
  const ctx = useContext(PliableAuthContext);
  if (!ctx) throw new Error('usePliableTenantsState must be used within PliableAuthProvider');
  return ctx.tenantsState;
}
